import { ref, onBeforeUnmount } from 'vue'
import type { DataTableFilterMeta } from 'primevue/datatable'
import type {
  PivotConfig,
  ChartOptions,
  HeatmapOptions,
  Comment,
  Version,
  OfflineChange,
  CollaborationUser,
  TableColumn,
  VersionChange
} from '../types'

interface DataTableProps {
  columns: TableColumn[]
  versionControl?: {
    enabled: boolean
    maxVersions: number
    autoSave: boolean
  }
  collaborationConfig?: {
    enabled: boolean
    websocketUrl: string
    syncInterval: number
    userTracking: boolean
  }
  offlineMode?: boolean
  syncConfig?: {
    interval: number
    retryAttempts: number
    conflictResolution: 'client' | 'server' | 'manual'
  }
}

type EmitEvents = {
  'pivot-change': PivotConfig
  'version-change': Version
  'comment-add': Comment
  'sync': OfflineChange[]
  'collaboration-update': CollaborationUser
}

type EmitFunction = <T extends keyof EmitEvents>(event: T, payload: EmitEvents[T]) => void

export const usePivot = (_props: DataTableProps, emit: EmitFunction) => {
  const pivotConfig = ref<PivotConfig | null>(null)
  const isPivotMode = ref(false)

  const applyPivot = (config: PivotConfig) => {
    pivotConfig.value = config
    isPivotMode.value = true
    emit('pivot-change', config)
  }

  const resetPivot = () => {
    pivotConfig.value = null
    isPivotMode.value = false
  }

  const computePivotData = (data: unknown[], config: PivotConfig) => {
    const rowGroups = new Map()

    data.forEach(item => {
      let currentGroup = rowGroups

      config.rowFields.forEach(field => {
        const value = (item as Record<string, unknown>)[field]
        if (!currentGroup.has(value)) {
          currentGroup.set(value, new Map())
        }
        currentGroup = currentGroup.get(value)
      })

      config.valueFields.forEach(field => {
        const value = Number((item as Record<string, unknown>)[field]) || 0
        const aggregation = config.aggregations[field] || 'sum'

        if (!currentGroup.has(field)) {
          currentGroup.set(field, { value: 0, count: 0 })
        }

        const stats = currentGroup.get(field)
        stats.value += value
        stats.count += 1

        if (aggregation === 'avg') {
          stats.aggregate = stats.value / stats.count
        } else {
          stats.aggregate = stats.value
        }
      })
    })

    return rowGroups
  }

  return {
    pivotConfig,
    isPivotMode,
    applyPivot,
    resetPivot,
    computePivotData
  }
}

export const useVisualization = () => {
  const chartConfig = ref<ChartOptions | null>(null)
  const heatmapConfig = ref<HeatmapOptions | null>(null)
  const chartRefs = ref<Map<string, HTMLCanvasElement | null>>(new Map())

  const applyChartConfig = (config: ChartOptions) => {
    chartConfig.value = config
  }

  const applyHeatmapConfig = (config: HeatmapOptions) => {
    heatmapConfig.value = config
  }

  const getHeatmapStyle = (value: number, config: HeatmapOptions) => {
    if (value === null || value === undefined) return {}

    const { minValue, maxValue, colorScale } = config
    const normalizedValue = (value - minValue) / (maxValue - minValue)
    const colorIndex = Math.floor(normalizedValue * (colorScale.length - 1))

    return {
      backgroundColor: colorScale[colorIndex],
      opacity: 0.8 + (normalizedValue * 0.2)
    }
  }

  const clearCharts = () => {
    chartRefs.value.clear()
  }

  const registerChartRef = (key: string, element: HTMLCanvasElement | null) => {
    if (element) {
      chartRefs.value.set(key, element)
    }
  }

  const unregisterChartRef = (key: string) => {
    chartRefs.value.delete(key)
  }

  return {
    chartConfig,
    heatmapConfig,
    chartRefs,
    applyChartConfig,
    applyHeatmapConfig,
    getHeatmapStyle,
    clearCharts,
    registerChartRef,
    unregisterChartRef
  }
}

export const useCollaboration = (props: DataTableProps, emit: EmitFunction) => {
  const isOffline = ref(false)
  const activeUsers = ref<CollaborationUser[]>([])
  const activeEditors = ref<Map<string, CollaborationUser>>(new Map())
  const websocket = ref<WebSocket | null>(null)
  const reconnectAttempts = ref(0)
  const maxReconnectAttempts = 5
  const reconnectTimeout = ref<number | null>(null)

  const connect = async () => {
    if (!props.collaborationConfig?.enabled || !props.collaborationConfig.websocketUrl) {
      return
    }

    try {
      websocket.value = new WebSocket(props.collaborationConfig.websocketUrl)

      websocket.value.onopen = () => {
        isOffline.value = false
        reconnectAttempts.value = 0
      }

      websocket.value.onclose = () => {
        isOffline.value = true
        attemptReconnect()
      }

      websocket.value.onmessage = (event) => {
        const message = JSON.parse(event.data)
        handleWebSocketMessage(message)
      }

      websocket.value.onerror = () => {
        isOffline.value = true
      }
    } catch (error) {
      console.error('WebSocket connection failed:', error)
      isOffline.value = true
    }
  }

  const disconnect = () => {
    if (websocket.value) {
      websocket.value.close()
      websocket.value = null
    }
    if (reconnectTimeout.value) {
      window.clearTimeout(reconnectTimeout.value)
      reconnectTimeout.value = null
    }
    activeUsers.value = []
    activeEditors.value.clear()
  }

  const attemptReconnect = () => {
    if (reconnectAttempts.value >= maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      return
    }

    reconnectTimeout.value = window.setTimeout(() => {
      reconnectAttempts.value++
      connect()
    }, 1000 * Math.pow(2, reconnectAttempts.value))
  }

  const handleWebSocketMessage = (message: { type: string; payload: unknown }) => {
    switch (message.type) {
      case 'users':
        activeUsers.value = message.payload as CollaborationUser[]
        break
      case 'edit':
        emit('collaboration-update', message.payload as CollaborationUser)
        break
      case 'comment':
        emit('comment-add', message.payload as Comment)
        break
    }
  }

  const getActiveEditor = (rowIndex: number, field: string): CollaborationUser | null => {
    const key = `${rowIndex}-${field}`
    return activeEditors.value.get(key) || null
  }

  const addComment = async (comment: Omit<Comment, 'id' | 'timestamp'>): Promise<Comment> => {
    const newComment: Comment = {
      id: Date.now().toString(),
      timestamp: new Date(),
      ...comment
    }
    emit('comment-add', newComment)
    return newComment
  }

  onBeforeUnmount(() => {
    disconnect()
  })

  return {
    isOffline,
    activeUsers,
    connect,
    disconnect,
    getActiveEditor,
    addComment
  }
}

export const useOfflineSync = (props: DataTableProps, emit: EmitFunction) => {
  const pendingChanges = ref<OfflineChange[]>([])
  const isSyncing = ref(false)
  const syncInterval = ref<number | null>(null)
  const retryCount = ref(0)
  const lastSyncTimestamp = ref<Date | null>(null)

  const startSync = () => {
    if (!props.offlineMode || !props.syncConfig) return

    syncInterval.value = window.setInterval(() => {
      void sync()
    }, props.syncConfig.interval)
  }

  const stopSync = () => {
    if (syncInterval.value) {
      window.clearInterval(syncInterval.value)
      syncInterval.value = null
    }
    retryCount.value = 0
  }

  const sync = async () => {
    if (isSyncing.value || pendingChanges.value.length === 0) return

    isSyncing.value = true

    try {
      emit('sync', pendingChanges.value)
      pendingChanges.value = []
      lastSyncTimestamp.value = new Date()
      retryCount.value = 0
    } catch (error) {
      console.error('Sync failed:', error)
      handleSyncError()
    } finally {
      isSyncing.value = false
    }
  }

  const handleSyncError = () => {
    if (!props.syncConfig) return

    retryCount.value++

    if (retryCount.value < props.syncConfig.retryAttempts) {
      const delay = Math.pow(2, retryCount.value) * 1000
      setTimeout(() => {
        void sync()
      }, delay)
    }
  }

  onBeforeUnmount(() => {
    stopSync()
  })

  return {
    pendingChanges,
    isSyncing,
    lastSyncTimestamp,
    startSync,
    stopSync,
    sync
  }
}

export const useVersionControl = (props: DataTableProps, emit: EmitFunction) => {
  const currentVersion = ref<Version | null>(null)
  const versionHistory = ref<Version[]>([])
  const autoSaveInterval = ref<number | null>(null)
  const unsavedChanges = ref<VersionChange[]>([])
  const isRestoring = ref(false)

  const saveVersion = async (changes: VersionChange[] = []): Promise<Version> => {
    if (!props.versionControl?.enabled) {
      throw new Error('Version control is not enabled')
    }

    const newVersion: Version = {
      id: Date.now().toString(),
      timestamp: new Date(),
      user: 'current-user',
      changes: [...unsavedChanges.value, ...changes]
    }

    versionHistory.value.push(newVersion)
    currentVersion.value = newVersion

    if (props.versionControl.maxVersions > 0) {
      while (versionHistory.value.length > props.versionControl.maxVersions) {
        versionHistory.value.shift()
      }
    }

    unsavedChanges.value = []
    emit('version-change', newVersion)
    return newVersion
  }

  const restoreVersion = async (version: Version): Promise<void> => {
    if (!props.versionControl?.enabled) {
      throw new Error('Version control is not enabled')
    }

    try {
      isRestoring.value = true

      if (currentVersion.value && unsavedChanges.value.length > 0) {
        await saveVersion()
      }

      currentVersion.value = version
      emit('version-change', version)
    } finally {
      isRestoring.value = false
    }
  }

  const startAutoSave = () => {
    if (props.versionControl?.enabled && props.versionControl.autoSave) {
      stopAutoSave()
      autoSaveInterval.value = window.setInterval(() => {
        if (unsavedChanges.value.length > 0) {
          void saveVersion()
        }
      }, 5 * 60 * 1000)
    }
  }

  const stopAutoSave = () => {
    if (autoSaveInterval.value) {
      window.clearInterval(autoSaveInterval.value)
      autoSaveInterval.value = null
    }
  }

  onBeforeUnmount(() => {
    stopAutoSave()
    if (unsavedChanges.value.length > 0) {
      void saveVersion()
    }
  })

  return {
    currentVersion,
    versionHistory,
    isRestoring,
    saveVersion,
    restoreVersion,
    startAutoSave,
    stopAutoSave
  }
}

export { useDataTable } from './useDataTable'
